import React from 'react';
import { Box, Typography } from '@mui/material';
import CertificateHeader from './CertificateHeader';

/**
 * Bonafide Certificate - Landscape Side-by-Side Template
 * Prints 2 identical certificates side-by-side on an A4 Landscape page.
 */
export const BonafideTemplateLandscape = ({ data }) => {
    const { student, details, issueDate, academicYear, division } = data;

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) return dateStr;
        const match = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (match) {
            return `${match[3]}/${match[2]}/${match[1]}`;
        }
        try {
            const date = new Date(dateStr);
            if (isNaN(date.getTime())) return dateStr;
            const d = String(date.getDate()).padStart(2, '0');
            const m = String(date.getMonth() + 1).padStart(2, '0');
            const y = date.getFullYear();
            return `${d}/${m}/${y}`;
        } catch (e) {
            return dateStr;
        }
    };

    const formatDateInWords = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        const months = ['January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'];
        const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
            'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen',
            'Seventeen', 'Eighteen', 'Nineteen'];
        const tens = ['', '', 'Twenty', 'Thirty'];
        const day = date.getDate();
        const month = months[date.getMonth()];
        const year = date.getFullYear();

        const dayWord = day < 20 ? ones[day] : tens[Math.floor(day / 10)] + (day % 10 ? ' ' + ones[day % 10] : '');
        const yearSuffix = year - 2000;
        const yearWord = yearSuffix < 20 ? ones[yearSuffix] : tens[Math.floor(yearSuffix / 10)] + (yearSuffix % 10 ? ' ' + ones[yearSuffix % 10] : '');

        return `(${dayWord} ${month} Two Thousand ${yearWord})`;
    };

    const renderSingleCertificate = (isLeft) => (
        <Box sx={{
            width: '48%',
            boxSizing: 'border-box',
            border: '1px solid #000',
            p: 1.5,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            height: '100%',
            backgroundColor: '#fff',
        }}>
            {/* Unified Certificate Header */}
            <CertificateHeader isLeavingCertificate={false} details={details} />

            {/* Certificate Title */}
            <Typography sx={{
                textAlign: 'center', fontSize: 13, fontWeight: 'bold',
                textDecoration: 'underline', mb: 1, fontFamily: 'inherit'
            }}>
                Bonafide Certificate
            </Typography>

            {/* Reg No / Sr No row */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, px: 0.5, fontSize: 10 }}>
                <Typography sx={{ fontSize: 10, fontFamily: 'inherit' }}>
                    <strong>Reg No: {student?.admissionNumber || '___'}</strong>
                </Typography>
                <Typography sx={{ fontSize: 10, fontFamily: 'inherit', color: '#e53935' }}>
                    <strong>Sr No : {details?.srNumber ? String(details.srNumber).padStart(5, '0') : '00001'}</strong>
                </Typography>
            </Box>

            {/* Body Intro */}
            <Box sx={{ px: 0.5, mb: 1 }}>
                <Typography sx={{ fontSize: 10, fontWeight: 'bold', mb: 0.2, fontFamily: 'inherit' }}>
                    This is to certify that,
                </Typography>
                <Typography sx={{ fontSize: 10, mb: 1, fontFamily: 'inherit' }}>
                    The details of Bonafide Student of this School/College are given below.
                </Typography>
            </Box>

            {/* Table + Photo */}
            <Box sx={{ display: 'flex', gap: 1.5, px: 0.5, mb: 1 }}>
                <Box sx={{ flex: 1 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <tbody>
                            <DetailRow label="Student Full Name" value={student?.name} />
                            <DetailRow label="Mother Name" value={student?.motherName || details?.motherName} />
                            <DetailRow label="Standard & Division" value={`${details?.currentClass || ''} ${division || details?.currentDivision || ''}`} />
                            <DetailRow label="Birth Date(In Figure)" value={formatDate(details?.dateOfBirth)} />
                            <DetailRow label="Birth Date(In Words)" value={details?.dateOfBirthInWords || formatDateInWords(details?.dateOfBirth)} />
                            <DetailRow label="Place Of Birth" value={details?.placeOfBirth} />
                            <DetailRow label="Aadhar Card No" value={details?.uid} />
                            <DetailRow label="Cast" value={details?.caste} />
                            <DetailRow label="Pen No." value={details?.penNumber || 'null'} />
                        </tbody>
                    </table>
                </Box>
                {/* Photo Placeholder */}
                <Box sx={{
                    width: 75, height: 95, border: '1px solid #000',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 10, flexShrink: 0, mt: 0.5
                }}>
                    Photo
                </Box>
            </Box>

            {/* Character Statement */}
            <Typography sx={{ fontSize: 10, mt: 1, mb: 2, px: 0.5, fontWeight: 'bold', fontFamily: 'inherit' }}>
                To the best of my knowledge he/she bears a good moral characters.
            </Typography>

            {/* Signatures */}
            <Box sx={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end',
                mt: 3, px: 0.5, fontFamily: 'inherit'
            }}>
                <Box sx={{ minWidth: 80 }}>
                    <Typography sx={{ fontSize: 9 }}>Date : {issueDate ? formatDate(issueDate) : formatDate(new Date())}</Typography>
                    <Typography sx={{ fontSize: 9 }}>Place :</Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                    <Typography sx={{ fontSize: 10 }}>Clerk</Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                    <Typography sx={{ fontSize: 10 }}>Teacher</Typography>
                </Box>
                <Box sx={{ textAlign: 'right', pr: '50px' }}>
                    <Typography sx={{ fontSize: 10, fontWeight: 'bold' }}>Principal</Typography>
                    <Typography sx={{ fontSize: 9 }}>Vidya Pratishthan's</Typography>
                    <Typography sx={{ fontSize: 9 }}>Supe English Medium School</Typography>
                </Box>
            </Box>
        </Box>
    );

    return (
        <Box sx={{
            fontFamily: '"Times New Roman", Georgia, serif',
            color: '#000',
            backgroundColor: '#fff',
            p: '5mm',
            width: '297mm',
            height: '210mm',
            boxSizing: 'border-box',
            display: 'flex',
            justifyContent: 'space-between',
            gap: '4%',
            '@media print': {
                width: '297mm',
                height: '210mm',
                margin: 0,
                padding: '5mm',
                '@page': {
                    size: 'A4 landscape',
                    margin: 0
                }
            }
        }}>
            {renderSingleCertificate(true)}
            {renderSingleCertificate(false)}
        </Box>
    );
};

// Helper component for detail rows
const DetailRow = ({ label, value }) => (
    <tr>
        <td style={{
            padding: '2px 0', fontSize: 9, fontWeight: 'bold',
            width: '40%', verticalAlign: 'top'
        }}>
            {label}
        </td>
        <td style={{ padding: '2px 4px', fontSize: 9, width: 10 }}>:</td>
        <td style={{
            padding: '2px 0', fontSize: 9,
            fontWeight: 600
        }}>
            {value || '-'}
        </td>
    </tr>
);

export default BonafideTemplateLandscape;
